import type { RefObject } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { KbAdminEditorSectionProps } from "../kbAdminEditorProps";

type Props = Pick<
  KbAdminEditorSectionProps,
  | "chunkQuality"
  | "chunkQualityAdvice"
  | "articleMode"
  | "setArticleMode"
  | "doArticle"
  | "hasEditorName"
  | "articleSlug"
  | "publishedArticleLocked"
  | "loadedArticle"
  | "confirmDeleteSlug"
  | "setConfirmDeleteSlug"
  | "doSoftDelete"
  | "deleteWorking"
  | "showDevRescopePill"
  | "devRescopeBusy"
  | "onDevRescopeFromEditor"
  | "clearWorkspace"
  | "lastArticleId"
> & {
  saveSectionRef: RefObject<HTMLDivElement | null>;
  scrollToSaveSection: () => void;
};

export function KbEditorSaveDock(props: Props) {
  const {
    saveSectionRef,
    scrollToSaveSection,
    chunkQuality,
    chunkQualityAdvice,
    articleMode,
    setArticleMode,
    doArticle,
    hasEditorName,
    articleSlug,
    publishedArticleLocked,
    loadedArticle,
    confirmDeleteSlug,
    setConfirmDeleteSlug,
    doSoftDelete,
    deleteWorking,
    showDevRescopePill,
    devRescopeBusy,
    onDevRescopeFromEditor,
    clearWorkspace,
    lastArticleId,
  } = props;

  return (
    <>
      <div
        ref={saveSectionRef}
        id="kb-editor-save-section"
        tabIndex={-1}
        className="rounded-xl border-2 border-emerald-600 bg-emerald-50 px-4 py-4 space-y-3 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:border-emerald-500 dark:bg-emerald-950/40 dark:focus-visible:ring-emerald-400"
      >
        <div className="text-sm font-semibold text-emerald-950 dark:text-emerald-100">Save to the knowledge base</div>
        {chunkQuality ? (
          <div
            className={
              "rounded-lg border px-3 py-2 text-xs " +
              (chunkQuality.risk === "healthy"
                ? "border-emerald-300 bg-emerald-100/70 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/45 dark:text-emerald-100"
                : chunkQuality.risk === "moderate"
                  ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100"
                  : "border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950/45 dark:text-red-100")
            }
          >
            <div className="font-semibold">
              Chunk quality: {chunkQuality.risk} ({chunkQuality.totalChunkChars} total chars across {chunkQuality.chunkCount} chunks)
            </div>
            {chunkQualityAdvice.length ? <div className="mt-1">Advice: {chunkQualityAdvice.join(" ")}</div> : null}
          </div>
        ) : null}
        <p className="text-xs text-emerald-900/85 leading-relaxed dark:text-emerald-200/90">
          Step 1: <strong>Review search segments</strong> (optional)—see how the text will be chunked for retrieval. Step 2: choose <strong>Save / create</strong> to store the article.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="w-full sm:w-auto min-w-0 rounded-lg border border-emerald-300 bg-white px-2 py-2 text-sm text-emerald-950"
            value={articleMode}
            onChange={(event) => setArticleMode(event.target.value as any)}
          >
            <option value="preview">Review search segments (optional)</option>
            <option value="save">Save or create article</option>
          </select>
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "kb-editor-save" }}
            className="rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-50"
            onClick={() => void doArticle()}
            disabled={!hasEditorName}
            title={
              publishedArticleLocked && loadedArticle?.scope !== "org"
                ? "Published shared KB articles require an admin to edit"
                : publishedArticleLocked && loadedArticle?.scope === "org"
                  ? "Submits a change proposal for admin approval"
                  : undefined
            }
          >
            {articleMode === "save"
              ? articleSlug
                ? publishedArticleLocked && loadedArticle?.scope === "org"
                  ? "Submit for review"
                  : "Save article"
                : "Create article"
              : "Preview segments only"}
          </button>
          {articleSlug ? (
            confirmDeleteSlug === articleSlug ? (
              <>
                <span className="text-xs text-red-900 font-medium dark:text-red-200">Delete this article?</span>
                <button
                  type="button"
                  className="rounded-lg border border-red-400 bg-red-100 px-2 py-1 text-xs font-semibold text-red-900 disabled:opacity-50 dark:border-red-600 dark:bg-red-950/50 dark:text-red-100"
                  onClick={() => void doSoftDelete(articleSlug)}
                  disabled={deleteWorking}
                >
                  {deleteWorking ? "Deleting…" : "Confirm delete"}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                  onClick={() => setConfirmDeleteSlug(null)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:bg-red-950/35 dark:text-red-300 dark:hover:bg-red-950/55"
                onClick={() => setConfirmDeleteSlug(articleSlug)}
                disabled={deleteWorking}
              >
                Delete article
              </button>
            )
          ) : null}
          {showDevRescopePill ? (
            <a
              href="#kb-dev-rescope-panel"
              className="rounded-lg border border-amber-500 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-950 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/55"
              onClick={(e) => {
                e.preventDefault();
                if (devRescopeBusy) return;
                void onDevRescopeFromEditor?.();
              }}
              aria-busy={devRescopeBusy || undefined}
            >
              {devRescopeBusy ? "Opening Dev…" : "Dev rescope"}
            </a>
          ) : null}
          <button
            type="button"
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-950 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/55"
            onClick={clearWorkspace}
          >
            Clear workspace
          </button>
        </div>
        <div className="text-xs text-emerald-800/80 dark:text-emerald-200/85">URL slug: {articleSlug || "(generated from title when you save)"}</div>
      </div>

      <button
        type="button"
        className="fixed bottom-6 right-6 z-40 flex max-w-[min(100vw-2rem,20rem)] items-center gap-2 rounded-full border-2 border-emerald-700 bg-emerald-100 px-4 py-3 text-sm font-semibold text-emerald-950 shadow-lg shadow-emerald-900/15 hover:bg-emerald-200 focus-visible:outline focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:border-emerald-500 dark:bg-emerald-900/55 dark:text-emerald-50 dark:shadow-emerald-950/40 dark:hover:bg-emerald-800/65 dark:focus-visible:ring-emerald-400"
        onClick={scrollToSaveSection}
        aria-controls="kb-editor-save-section"
        aria-label="Scroll to Save and preview section"
      >
        <svg className="h-5 w-5 shrink-0 text-emerald-800 dark:text-emerald-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 5v14M19 12l-7 7-7-7" />
        </svg>
        <span className="text-left leading-tight">Save & preview</span>
      </button>

      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300">
        <div className="mt-1">Last saved article id: {lastArticleId || "none"}</div>
        {loadedArticle ? (
          <div className="mt-1">
            Loaded: status {loadedArticle.status ?? "?"} • scope {loadedArticle.scope ?? "?"} • type {loadedArticle.doc_type ?? "(none)"} • updated {loadedArticle.updated_at ?? "?"}
          </div>
        ) : (
          <div className="mt-1">No article loaded from the library.</div>
        )}
      </div>
    </>
  );
}
